import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TablePagination from '@mui/material/TablePagination';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { blogApi, blogKeys } from '@/features/blogs/api/blog.api';

export function AdminBlogsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isLoading, isError } = useQuery({
    queryKey: blogKeys.admin.list({ page: page + 1, limit: rowsPerPage }),
    queryFn: () => blogApi.listAdmin({ page: page + 1, limit: rowsPerPage }),
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      blogApi.update(id, { published }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: blogKeys.admin.all });
      void queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => blogApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: blogKeys.admin.all });
      void queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });

  const posts = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <AdminPageHeader
        title="Blog Management"
        subtitle="Create, edit, publish, and delete blog posts"
        actions={
          <Button
            component={RouterLink}
            to={ADMIN_ROUTES.BLOGS_NEW}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New blog
          </Button>
        }
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          Failed to load blogs.
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="center">Published</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && posts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No blogs yet. Create your first post.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {posts.map((post) => (
                <TableRow key={post.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{post.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      /blogs/{post.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={post.published ? 'Published' : 'Draft'}
                      color={post.published ? 'success' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>{dayjs(post.updatedAt).format('D MMM YYYY')}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={post.published}
                      onChange={(_, checked) =>
                        publishMutation.mutate({ id: post.id, published: checked })
                      }
                      disabled={publishMutation.isPending}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={RouterLink}
                      to={ADMIN_ROUTES.blogEdit(post.id)}
                      size="small"
                      aria-label="Edit blog"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Delete blog"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete “${post.title}”?`)) {
                          deleteMutation.mutate(post.id);
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </Paper>
    </Box>
  );
}
